<script setup>
import moment from 'moment-timezone'
import { mediaUrl } from '@core/utils/mediaUrl'
import {numberWithCommas} from '@/plugins/misc'
import {useUsersStore, useReservationsStore, useUserGroupsStore, useRestaurantsStore} from '@/stores'
import {onBeforeMount, onBeforeUnmount, onMounted, ref, computed} from 'vue'
import {storeToRefs} from 'pinia'
import {useRoute, useRouter} from "vue-router"
import {useMessagingStore} from '@/stores/messaging.store'
import noResEmptyState from '@/assets/images/ui/no_reservations_empty_state.jpg'
import googleAttribution from '@/assets/images/ui/google_on_white.png'
import Testimonials from "@core/components/Testimonials.vue"
import {requiredValidator} from "@validators"
import lodash from "underscore"

const route = useRoute()
const router = useRouter()

const usersStore = useUsersStore()
const messagingStore = useMessagingStore()
const {myUser, users, isRunningInStandAlone, memberSearchResults, suggestions} = storeToRefs(usersStore)

const networkTab = ref('friends')

onMounted(async () => {
  await usersStore.getFollowers()
  await usersStore.getFollowing()
  await usersStore.getFollowingPending()
})

const userGroupsStore = useUserGroupsStore()

const viewingDetailsMember = ref(null)

const memberSearchResult = ref(null)
const fetchingAutocomplete = ref(false)
const isConfirmDeleteDialogOpen = ref(false)
const friendToRemove = ref(null)

function confirmRemoveFriend(item) {
  friendToRemove.value = item
  isConfirmDeleteDialogOpen.value = true
}

async function removeFriend() {
  if (!friendToRemove.value) return
  if (friendToRemove.value.followedByMyUser) {
    await usersStore.unfollow(friendToRemove.value._id)
  } else {
    await usersStore.removeFollower(friendToRemove.value._id)
  }
  friendToRemove.value = null
  await refreshNetwork()
}

async function refreshNetwork() {
  await Promise.all([
    usersStore.getFollowers(),
    usersStore.getFollowing(),
    usersStore.getFollowingPending(),
  ])
}

async function handleApproveFollower(memberId) {
  await usersStore.approveFollower(memberId)
  await refreshNetwork()
}

async function handleRemoveFollower(memberId) {
  await usersStore.removeFollower(memberId)
  await refreshNetwork()
}

async function handleFollow(memberId) {
  await usersStore.follow(memberId)
  await refreshNetwork()
}

async function handleUnfollow(memberId) {
  await usersStore.unfollow(memberId)
  await refreshNetwork()
}

//computed
const followedByMyUser = computed(() => {
  let followedUsers = users.value.filter(u => (u.followedByMyUser && !u.followedByMyUserPending))
  followedUsers.sort((a, b) => {
    if (a.firstName < b.firstName) return -1
    if (a.firstName > b.firstName) return 1

    return 0
  })

  return followedUsers
})

const followedByMyUserPending = computed(() => {
  let followedByMyUserPendings = users.value.filter(u => u.followedByMyUserPending)

  //order by followedByMyUserPending first then by first name
  followedByMyUserPendings.sort((a, b) => {
    if (a.firstName < b.firstName) return -1
    if (a.firstName > b.firstName) return 1

    return 0
  })

  return followedByMyUserPendings
})

const followingMyUser = computed(() => {
  let followingUsers = users.value.filter(u => (u.followingMyUser && followerApproved(u._id)))

  //order by followedByMyUserPending first then by first name
  followingUsers.sort((a, b) => {
    if (a.firstName < b.firstName) return -1
    if (a.firstName > b.firstName) return 1

    return 0
  })

  return followingUsers
})

const followingMyUserPending = computed(() => {
  let followingMyUserPendings = users.value.filter(u => u.followingMyUser && !followerApproved(u._id))

  //order by followedByMyUserPending first then by first name
  followingMyUserPendings.sort((a, b) => {
    if (a.firstName < b.firstName) return -1
    if (a.firstName > b.firstName) return 1

    return 0
  })

  return followingMyUserPendings
})

const friends = computed(() => {
  const combined = new Map()
  followedByMyUser.value.forEach(u => combined.set(u._id, u))
  followingMyUser.value.forEach(u => {
    if (!combined.has(u._id)) combined.set(u._id, u)
  })
  const result = Array.from(combined.values())
  result.sort((a, b) => {
    if (a.firstName < b.firstName) return -1
    if (a.firstName > b.firstName) return 1
    
    return 0
  })
  
  return result
})

const isFollowedByMyUser = userId => {
  return followedByMyUser.value.find(u => u._id == userId)
}
const isFollowedByMyUserPending = userId => {
  return followedByMyUserPending.value.find(u => u._id == userId)
}

// Spouse/secondary pairs are permanently connected — the remove control is
// hidden for the linked account and replaced with a non-actionable indicator.
const isMySpouse = userId => !!usersStore.mySpouseId && String(userId) === usersStore.mySpouseId

const followerApproved = userId => {
  return myUser?.value?.followersApproved?.includes(userId)
}

const isFollowingMyUser = userId => {
  return users.value.find(u => u._id == userId)?.followingMyUser
}

const openProfile = () => {
  //find user from users where _id = memberSearchResult.value._id then set viewingDetailsMember.value to that object
  viewingDetailsMember.value = users.value.find(u => u._id == memberSearchResult.value._id)
}

const fetchAutocomplete = lodash.throttle(async input => {
  if (!input) return
  fetchingAutocomplete.value = true
  await usersStore.searchMembers(input)
  fetchingAutocomplete.value = false
}, 500)

const isMutualFriend = item =>
  item.followedByMyUser && item.followingMyUser && followerApproved(item._id)

const messagingUser = ref(null)

async function startMessage(item) {
  messagingUser.value = item._id
  try {
    const conv = await messagingStore.createConversation({
      type: 'member-member',
      memberIds: [item._id],
    })
    router.push({ path: '/messages', query: { conv: conv._id } })
  } catch (err) {
    console.error('startMessage error', err)
  } finally {
    messagingUser.value = null
  }
}

function showPlatinumBadge(user) {
  return user?.isPlatinum || user?.membershipType === 'Platinum' || user?.membershipType === 'Trial'
}

function memberCanGift() {
  if (!myUser.value) return false

  //group
  const userGroup = myUser.value.userGroups?.find(group => group.type === 'enterprise' || group.type === 'friend')
  const subscription = userGroup?.platinumSubscriptionsByState.current?.status === 'Active' && userGroup.platinumSubscriptionsByState.current?.planId === 'enterprise-1cycle-annual'

  // primary
  const primaryAcc = !myUser.value.isSecondaryAccount && myUser.value.platinumSubscriptionsByState.current?.status === 'Active' && myUser.value.platinumSubscriptionsByState.current?.planId === 'individual-1cycle-annual'

  // secondary
  const secondaryAcc = myUser.value.isSecondaryAccount && myUser.value.platinumSubscriptionsByState.current?.status === 'Active' && (myUser.value.platinumSubscriptionsByState.current?.planId === 'individual-1cycle-annual' || myUser.value.platinumSubscriptionsByState.current?.planId === 'enterprise-1cycle-annual')

  // restaurants
  const restaurantAcc = myUser.value.primaryGroupType === 'restaurant' && myUser.value.isPlatinum

  return userGroup && subscription || primaryAcc || secondaryAcc || restaurantAcc || myUser.value.userLevel === 'admin'
}
</script>

<template>
  <div>
    <ConfirmDialog
      v-model:is-dialog-visible="isConfirmDeleteDialogOpen"
      confirmation-msg="Are you sure you want to remove this connection?"
      confirm-btn-text="Yes"
      cancel-btn-text="No"
      @confirm="removeFriend"
    />

    <div class="content-wrapper">
      <a
        class="back-to-profile"
        @click="router.back()"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        ><path d="m15 18-6-6 6-6" /></svg>
        Back
      </a>
      <div v-if="false">
        <div class="loading-logo">
          <img
            src="/logo_no_border.png"
            height="100%"
            alt="Logo"
          >
        </div>
        <div class="loading">
          <div class="effect-1 effects" />
          <div class="effect-2 effects" />
          <div class="effect-3 effects" />
        </div>
      </div>
      <div v-else>
        <div class="mt-4">
          <VAutocomplete
            v-model="memberSearchResult"
            placeholder="Find Connections"
            :items="memberSearchResults"
            item-title="fullName"
            :return-object="true"
            :no-filter="true"
            :hide-no-data="true"
            :clearable="true"
            :persistent-clear="true"
            :loading="fetchingAutocomplete"
            :menu-icon="null"
            variant="solo-filled"
            flat
            rounded
            hide-details
            class="max-content-width network-search"
            style="margin-bottom: 1em !important;"
            @update:search="fetchAutocomplete"
            @update:model-value="openProfile"
            @click:clear="memberSearchResults = []"
            @update:focused="!memberSearchResult ? memberSearchResults = [] : null"
          >
            <template #prepend-inner>
              <VIcon>mdi-magnify</VIcon>
            </template>
            <template #item="{ item, props }">
              <VListItem
                v-bind="props"
                class="network-person-row"
                :prepend-avatar="mediaUrl(item.raw.profileImageId, 'thumb')"
              >
                <template #title>
                  <span class="text-sm name-line">
                    {{ item.raw?.firstName + ' ' + item.raw?.lastName }}
                    <VIcon
                      v-if="item.raw?.verified"
                      size="16"
                      class="verified-check"
                    >
                      material-symbols:verified
                    </VIcon>
                    <span
                      v-if="showPlatinumBadge(item.raw)"
                      class="text-xs platinum-label"
                    >
                      (Platinum)
                    </span>
                  </span>
                </template>
                <template #subtitle>
                  <span class="text-xs">
                    {{ (item.raw?.company ? item.raw?.company : '') }}
                  </span>
                </template>
                <template
                  v-if="!item.raw.profileImageId && (item.raw?.firstName?.length > 0 || item.raw?.lastName?.length > 0)"
                  #prepend
                >
                  <div class="avatar-search rounded-circle">
                    <div class="avatar-fallback flex flex-row align-center justify-center">
                      <span class="avatar-fallback-text">{{ item.raw?.firstName?.[0]?.toUpperCase() }}{{ item.raw?.lastName?.[0]?.toUpperCase() }}</span>
                    </div>
                  </div>
                </template>
                <template #append>
                  <div class="network-member-action-wrap">
                    <VChip
                      v-if="isMySpouse(item.raw._id)"
                      size="small"
                      variant="tonal"
                      color="secondary"
                      class="network-member-action-btn"
                    >
                      Spouse
                    </VChip>
                    <VBtn
                      v-else-if="isFollowedByMyUser(item.raw._id) || isFollowedByMyUserPending(item.raw._id)"
                      variant="tonal"
                      size="small"
                      width="fit-content"
                      density="compact"
                      class="profile-header-edit-button network-member-action-btn"
                      color="secondary"
                      style="padding-left: 5px !important; padding-right: 5px !important;"
                      @click.stop="handleUnfollow(item.raw._id)"
                    >
                      {{ isFollowedByMyUserPending(item.raw._id) ? 'Remove' : 'Connected' }}
                    </VBtn>
                    <VBtn
                      v-else-if="item.raw._id != myUser._id"
                      variant="tonal"
                      size="small"
                      width="fit-content"
                      aria-label="Add Connection"
                      class="profile-header-edit-button network-member-action-btn network-add-friend-btn"
                      style="padding: 4px 12px !important;"
                      @click.stop="handleFollow(item.raw._id)"
                    >
                      <VIcon size="18">
                        mdi-account-plus
                      </VIcon>
                    </VBtn>
                  </div>
                </template>
              </VListItem>
            </template>
          </VAutocomplete>

          <div class="max-content-width position-relative">
            <VTabs
              v-model="networkTab"
              class="mb-2"
            >
              <VTab value="friends">
                Connections ({{ friends.length }})
              </VTab>
              <VTab value="suggestions">
                Suggested
              </VTab>
            </VTabs>
            <VDivider />

            <VWindow v-model="networkTab">
              <!-- Friends tab -->
              <VWindowItem value="friends">
                <div
                  v-if="friends.length === 0 && followingMyUserPending.length === 0 && followedByMyUserPending.length === 0"
                  class="text-center py-10 px-4"
                >
                  <VIcon
                    size="48"
                    color="grey"
                    class="mb-4"
                  >
                    mdi-account-multiple-outline
                  </VIcon>
                  <p class="text-body-1 text-medium-emphasis">
                    Connect &amp; discover their favorites
                  </p>
                </div>
                <div
                  v-else
                  class="pb-3"
                >
                  <VListSubheader v-if="followingMyUserPending?.length > 0">
                    Connection Requests
                  </VListSubheader>
                  <VVirtualScroll
                    v-if="followingMyUserPending?.length > 0"
                    :items="followingMyUserPending"
                    max-height="600"
                    class="mt-2"
                  >
                    <template #default="{ item }">
                      <VListItem
                        class="pl-1 pr-0 network-person-row"
                        @click="viewingDetailsMember = item; "
                      >
                        <template #prepend>
                          <VAvatar
                            v-if="item.profileImageId"
                            size="48"
                            class="list-avatar"
                          >
                            <VImg :src="mediaUrl(item.profileImageId, 'thumb')" />
                          </VAvatar>
                          <VAvatar
                            v-else
                            size="48"
                            class="list-avatar avatar-initials"
                          >
                            <span class="avatar-fallback-text">{{ item?.firstName?.[0]?.toUpperCase() }}{{ item?.lastName?.[0]?.toUpperCase() }}</span>
                          </VAvatar>
                        </template>

                        <template #title>
                          <span class="text-sm name-line">
                            {{ item.firstName + ' ' + item.lastName }}
                            <VIcon
                              v-if="item.verified"
                              size="14"
                              class="verified-check"
                            >
                              material-symbols:verified
                            </VIcon>
                            <span
                              v-if="showPlatinumBadge(item)"
                              class="text-xs platinum-label"
                            >
                              (Platinum)
                            </span>
                          </span>
                        </template>
                        <template #subtitle>
                          <span class="text-xs">
                            {{ (item.company ? item.company : '') }}
                            <div
                              v-if="item.favoritedRestaurantsCount > 0"
                              class="text-medium-emphasis"
                            >
                              {{ item.favoritedRestaurantsCount }} Favorites
                            </div>
                          </span>
                        </template>
                        <template #append>
                          <VBtn
                            variant="tonal"
                            size="small"
                            width="fit-content"
                            density="compact"
                            class="ml-2"
                            style="padding-left: 5px !important; padding-right: 5px !important;"
                            @click.stop="handleApproveFollower(item._id)"
                          >
                            Confirm
                          </VBtn>
                          <VBtn
                            variant="tonal"
                            size="small"
                            width="fit-content"
                            density="compact"
                            color="secondary"
                            class="ml-2"
                            style="padding-left: 5px !important; padding-right: 5px !important;"
                            @click.stop="handleRemoveFollower(item._id)"
                          >
                            Delete
                          </VBtn>
                        </template>
                      </VListItem>
                    </template>
                  </VVirtualScroll>
                  <VDivider v-if="followingMyUserPending?.length > 0" />
                  <VVirtualScroll
                    :items="friends"
                    max-height="600"
                    class="mt-2"
                  >
                    <template #default="{ item }">
                      <VListItem
                        class="pl-1 pr-0 network-person-row"
                        @click="viewingDetailsMember = item; "
                      >
                        <template #prepend>
                          <VAvatar
                            v-if="item.profileImageId"
                            size="48"
                            class="list-avatar"
                          >
                            <VImg :src="mediaUrl(item.profileImageId, 'thumb')" />
                          </VAvatar>
                          <VAvatar
                            v-else
                            size="48"
                            class="list-avatar avatar-initials"
                          >
                            <span class="avatar-fallback-text">{{ item?.firstName?.[0]?.toUpperCase() }}{{ item?.lastName?.[0]?.toUpperCase() }}</span>
                          </VAvatar>
                        </template>

                        <template #title>
                          <span class="text-sm name-line">
                            {{ item.firstName + ' ' + item.lastName }}
                            <VIcon
                              v-if="item.verified"
                              size="14"
                              class="verified-check"
                            >
                              material-symbols:verified
                            </VIcon>
                            <span
                              v-if="showPlatinumBadge(item)"
                              class="text-xs platinum-label"
                            >
                              (Platinum)
                            </span>
                            <span v-if="item.followingMyUser && followerApproved(item._id) && !item.followedByMyUser && !item.followedByMyUserPending"> - <span
                              class="text-primary cursor-pointer"
                              @click.stop="handleFollow(item._id)"
                            >Add Connection</span></span>
                          </span>
                        </template>
                        <template #subtitle>
                          <span class="text-xs">
                            {{ (item.company ? item.company : '') }}
                            <div
                              v-if="item.favoritedRestaurantsCount > 0"
                              class="text-medium-emphasis"
                            >
                              {{ item.favoritedRestaurantsCount }} Favorites
                            </div>
                          </span>
                        </template>
                        <template #append>
                          <div
                            class="d-flex align-center gap-1"
                            @click.stop
                          >
                            <!-- Spouse: permanently connected, no remove control -->
                            <VChip
                              v-if="isMySpouse(item._id)"
                              size="small"
                              variant="tonal"
                              color="secondary"
                            >
                              Spouse
                            </VChip>

                            <!-- Message button (mutual friends only) -->
                            <VBtn
                              v-if="isMutualFriend(item)"
                              size="small"
                              variant="tonal"
                              color="primary"
                              class="icon-square-btn"
                              :loading="messagingUser === item._id"
                              @click.stop="startMessage(item)"
                            >
                              <VIcon
                                size="18"
                                icon="mdi-message-text-outline"
                              />
                            </VBtn>

                            <!-- 3-dot menu -->
                            <VMenu location="bottom end">
                              <template #activator="{ props: menuProps }">
                                <VBtn
                                  v-bind="menuProps"
                                  icon
                                  size="x-small"
                                  variant="text"
                                  density="compact"
                                >
                                  <VIcon size="18">
                                    mdi-dots-vertical
                                  </VIcon>
                                </VBtn>
                              </template>
                              <VList
                                density="compact"
                                min-width="160"
                              >
                                <VListItem
                                  v-if="item.profileId"
                                  prepend-icon="mdi-heart-outline"
                                  title="See Favorites"
                                  @click="router.push(`/m/${item.profileId}`)"
                                />
                                <VListItem
                                  v-if="!isMySpouse(item._id)"
                                  prepend-icon="mdi-account-remove-outline"
                                  title="Remove"
                                  @click="confirmRemoveFriend(item)"
                                />
                              </VList>
                            </VMenu>
                          </div>
                        </template>
                      </VListItem>
                    </template>
                  </VVirtualScroll>
                  <VDivider
                    v-if="followedByMyUserPending?.length > 0"
                    class="mt-2"
                  />
                  <VListSubheader v-if="followedByMyUserPending?.length > 0">
                    Pending
                  </VListSubheader>
                  <VVirtualScroll
                    v-if="followedByMyUserPending?.length > 0"
                    :items="followedByMyUserPending"
                    max-height="600"
                    class="mt-2"
                  >
                    <template #default="{ item }">
                      <VListItem
                        class="pl-1 pr-0 network-person-row"
                        @click="viewingDetailsMember = item; "
                      >
                        <template #prepend>
                          <VAvatar
                            v-if="item.profileImageId"
                            size="48"
                            class="list-avatar"
                          >
                            <VImg :src="mediaUrl(item.profileImageId, 'thumb')" />
                          </VAvatar>
                          <VAvatar
                            v-else
                            size="48"
                            class="list-avatar avatar-initials"
                          >
                            <span class="avatar-fallback-text">{{ item?.firstName?.[0]?.toUpperCase() }}{{ item?.lastName?.[0]?.toUpperCase() }}</span>
                          </VAvatar>
                        </template>

                        <template #title>
                          <span class="text-sm name-line">
                            {{ item.firstName + ' ' + item.lastName }}
                            <VIcon
                              v-if="item.verified"
                              size="14"
                              class="verified-check"
                            >
                              material-symbols:verified
                            </VIcon>
                            <span
                              v-if="showPlatinumBadge(item)"
                              class="text-xs platinum-label"
                            >
                              (Platinum)
                            </span>
                          </span>
                        </template>
                        <template #subtitle>
                          <span class="text-xs">
                            {{ (item.company ? item.company : '') }}
                            <div
                              v-if="item.favoritedRestaurantsCount > 0"
                              class="text-medium-emphasis"
                            >
                              {{ item.favoritedRestaurantsCount }} Favorites
                            </div>
                          </span>
                        </template>
                        <template #append>
                          <VBtn
                            size="small"
                            variant="tonal"
                            color="secondary"
                            style="padding: 4px 12px !important;"
                            @click.stop="handleUnfollow(item._id)"
                          >
                            Rescind
                          </VBtn>
                        </template>
                      </VListItem>
                    </template>
                  </VVirtualScroll>
                </div>
              </VWindowItem>

              <!-- Suggestions tab -->
              <VWindowItem value="suggestions">
                <PeopleYouMayKnow
                  @view-member="viewingDetailsMember = $event"
                />
              </VWindowItem>
            </VWindow>
          </div>
        </div>
      </div>
    </div>

    <MemberDetailsDialog
      :member="viewingDetailsMember"
      @on-close="viewingDetailsMember = null"
    />
  </div>
</template>

<style lang="scss" scoped>
.back-to-profile {
  display: inline-flex;
  align-items: center;
  gap: var(--ds-space-1);
  padding: var(--ds-space-2) 0 var(--ds-space-1);
  font-size: var(--ds-text-base);
  font-weight: 500;
  color: var(--ds-brand-primary-dark, #3F998F);
  text-decoration: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.name-line .verified-check {
  margin-left: 0px;
  margin-top: -2px;
  color: rgb(0, 149, 246);
}

.name-line .platinum-label {
  margin-left: 0px;
}

.list-avatar {
  margin-inline-end: 2px;
  margin-left: 2px;
}

.friends-loading-spinner {
  position: absolute;
  right: 5px;
  top: var(--ds-space-5);
}

.platinum-icon {
  color: var(--ds-brand-primary-dark, #3F998F) !important;
}

.avatar-initials {
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 2px;
}

.avatar-fallback-text {
  font-size: 1rem;
  font-weight: 500;
}

.avatar-search {
  cursor: pointer;
  position: relative;
  width: 30px;
  height: 30px;
  background: var(--ds-brand-primary-light);
  outline: thin solid rgba(69, 182, 169, 0.25);
  outline-offset: 2px;
  margin-inline-end: var(--ds-space-4);
  margin-left: 2px;
}

.avatar-search .avatar-fallback {
  width: 100%;
  height: 100%;
  font-size: 1rem;
}

.network-member-action-wrap {
  display: flex;
  margin-top: var(--ds-space-3);
}

.network-member-action-btn {
  width: 90px;
  min-height: 28px !important;
  height: 28px !important;
}

.network-add-friend-btn {
  width: var(--ds-space-8);
  min-width: var(--ds-space-8) !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

:deep(.v-virtual-scroll) {
  overflow-x: hidden;
}

:deep(.network-person-row .v-list-item__prepend) {
  width: auto;
}

:deep(.network-person-row .v-list-item__spacer) {
  width: var(--ds-space-1);
}

:deep(.network-person-row .v-list-item__append) {
  align-self: flex-end;
  padding-bottom: 2px;
}

.network-search :deep(.v-field) {
  font-size: 16px;
  background: var(--ds-surface-alt, #f8f8f9);
}

.icon-square-btn {
  min-width: var(--ds-space-8) !important;
  width: var(--ds-space-8) !important;
  min-height: var(--ds-space-8) !important;
  height: var(--ds-space-8) !important;
  padding: 0 !important;
  border-radius: var(--ds-radius-sm) !important;
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
